/**
 * PATH: packages/ui/src/hooks/navCustomizationStorage.ts
 * FILE_ID: UI-NAV-STORE-001
 * PURPOSE: localStorage read/write for the per-hub navigation
 *          customisation blob (`quotech_nav_customization`).
 * WHY: Extracted from useNavCustomization.ts (500-line ceiling) so the
 *      storage I/O — and the shape the hook keeps in memory — is one
 *      small unit shared by the hook and the settings UI.
 * FLOW:
 *   ┌─────────────┐   ┌──────────────┐   ┌──────────────────────────┐
 *   │ localStorage│ → │ readStorage  │ → │ InternalState (in-memory)│
 *   │   (blob)    │ ← │ writeStorage │ ← │ pins / order (hook-owned)│
 *   └─────────────┘   └──────────────┘   └──────────────────────────┘
 * DEPENDENCIES:
 *   - ../components/types: PinnedNavItem / UserCustomization shapes.
 * RELATED:
 *   - UI-NAV-HOOK-001 (useNavCustomization.ts): the consumer hook.
 *   - settings/NavigationTab + NavigationCustomization: the OTHER
 *     writers of this same key (they own `aiAutoOrder`).
 */

import type { PinnedNavItem, UserCustomization } from '../components/types'

export const STORAGE_KEY = 'quotech_nav_customization'

/**
 * Dispatched (by the settings UI that writes `aiAutoOrder` into the
 * customization blob) so that any live `useNavCustomization` consumer
 * in the SAME tab re-reads the flag immediately. The browser-native
 * `storage` event only fires in OTHER tabs, so this custom event is
 * the same-tab counterpart. Listening to both gives the operator
 * instant feedback when they toggle "Smart ordering" in Settings.
 */
export const NAV_CUSTOMIZATION_CHANGED_EVENT = 'quotech:nav-customization-changed'

/**
 * Notify same-tab `useNavCustomization` consumers that the
 * customization blob (esp. `aiAutoOrder`) just changed, so they re-read
 * the flag immediately. Call this right after any `localStorage` write
 * to `STORAGE_KEY` from the settings UI. No-op-safe (SSR / no window).
 */
export function notifyNavCustomizationChanged(): void {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(NAV_CUSTOMIZATION_CHANGED_EVENT))
    }
  } catch {
    // dispatch unavailable — cross-tab `storage` events still cover it.
  }
}

/** The slice of `UserCustomization` this hook keeps in memory. */
export interface NavCustomizationState {
  pinnedPages: PinnedNavItem[]
  navItemOrder: Record<string, string[]>
  /**
   * Flat list of nav-item ids the user has hidden from the rail
   * (right-click → "Hide from sidebar"). Filtered out of the rendered
   * nav and surfaced in a "Hidden (N)" restore tray so the action is
   * reversible. Defaults to `[]` (show everything). Mirrors backend
   * `UserNavigationPreferences.hidden_pages`.
   */
  hiddenIds: string[]
  /**
   * The user's "Smart ordering" (AI auto-order) preference, read from
   * the same `quotech_nav_customization` blob the settings modal
   * writes. STRICTLY defaults to `false` — auto-ordering is opt-in, so
   * a fresh user (or one who never toggled it) keeps the config-defined
   * order. Only an explicit stored `true` turns it on.
   */
  aiAutoOrder: boolean
}

/** Coerce an unknown blob field into a clean `string[]` (drops non-strings). */
function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}

export function readNavCustomization(): NavCustomizationState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { pinnedPages: [], navItemOrder: {}, hiddenIds: [], aiAutoOrder: false }
    const parsed = JSON.parse(raw) as Partial<UserCustomization>
    return {
      pinnedPages: Array.isArray(parsed.pinnedPages) ? parsed.pinnedPages : [],
      navItemOrder:
        parsed.navItemOrder && typeof parsed.navItemOrder === 'object'
          ? parsed.navItemOrder
          : {},
      hiddenIds: asStringList(parsed.hiddenPages),
      // Opt-in gate: only `=== true` enables it. Missing / undefined /
      // any non-true value ⇒ off (safe default per operator mandate).
      aiAutoOrder: parsed.aiAutoOrder === true,
    }
  } catch {
    return { pinnedPages: [], navItemOrder: {}, hiddenIds: [], aiAutoOrder: false }
  }
}

/**
 * Persist the hook-owned fields (pins + manual order + hidden) WITHOUT
 * clobbering the settings-owned fields (`aiAutoOrder`, theme, etc.) — the
 * spread of `existing` preserves them.
 */
export function writeNavCustomization(state: NavCustomizationState): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const existing = raw ? (JSON.parse(raw) as Partial<UserCustomization>) : {}
    const next: Partial<UserCustomization> = {
      ...existing,
      pinnedPages: state.pinnedPages,
      navItemOrder: state.navItemOrder,
      hiddenPages: state.hiddenIds,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable (Safari private mode etc.) — silent.
  }
}
